import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import {
  RefreshCw,
  Plus,
  Trash2,
  Award,
  TrendingUp,
  X,
  AlertCircle,
  Upload,
} from "lucide-react";

import { useTheme } from "../context/ThemeContext";
const Badges = () => {
  const { isDarkMode } = useTheme();
  const [badges, setBadges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedBadge, setSelectedBadge] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [filePreview, setFilePreview] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");
  const [showError, setShowError] = useState(false);
  const [formData, setFormData] = useState({
    key: "",
    name: "",
    description: "",
    count: 1,
    level: 0,
    iconUrl: "",
    category: "general",
    sortOrder: 0,
  });
  const charLimits = {
    key: 50,
    name: 100,
    description: 500,
    iconUrl: 500,
  };

  const badgeKeyOptions = [
    { value: "post", label: "Post", description: "Post creation milestones" },
    { value: "like", label: "Like", description: "Likes received milestones" },
    // { value: "share", label: "Share", description: "Share milestones" },
    {
      value: "followers",
      label: "Followers",
      description: "Follower count milestones",
    },
    {
      value: "email_verified",
      label: "Email Verified",
      description: "Email verification achievement",
    },
  ];

  const theme = {
    bg: isDarkMode ? "bg-black" : "bg-gray-50",
    cardBg: isDarkMode ? "bg-black" : "bg-white",
    textPrimary: isDarkMode ? "text-white" : "text-gray-900",
    textSecondary: isDarkMode ? "text-gray-400" : "text-gray-600",
    border: isDarkMode ? "border-gray-800" : "border-gray-200",
    hover: isDarkMode ? "hover:bg-neutral-900" : "hover:bg-gray-50",
    inputBg: isDarkMode ? "bg-neutral-900" : "bg-gray-100",
    inputText: isDarkMode ? "text-white" : "text-gray-900",
    tableBg: isDarkMode ? "bg-neutral-900" : "bg-gray-50",
  };

  const API_BASE_URL =
    process.env.REACT_APP_BASE_URL || "http://localhost:3000";

  const getAxiosConfig = () => {
    const token = localStorage.getItem("accessToken");
    return {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    };
  };

  const fetchBadges = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${API_BASE_URL}/badges/all`,
        getAxiosConfig()
      );

      if (
        response.data.statusCode === 401 ||
        response.data.message === "Unauthorized"
      ) {
        localStorage.removeItem("accessToken");
        localStorage.removeItem("refreshToken");
        localStorage.removeItem("user");
        window.location.href = "/login";
        return;
      }

      setBadges(response.data.badges || []);
    } catch (error) {
      if (
        error.response?.status === 401 ||
        error.response?.data?.message === "Unauthorized"
      ) {
        localStorage.removeItem("accessToken");
        localStorage.removeItem("refreshToken");
        localStorage.removeItem("user");
        window.location.href = "/login";
        return;
      }

      console.error("Error fetching badges:", error);
      setBadges([]);
    } finally {
      setLoading(false);
    }
  }, [API_BASE_URL]);
  useEffect(() => {
    fetchBadges();
  }, [fetchBadges]);

  const uploadMediaUsingPresigned = async (file) => {
    try {
      if (!file) {
        throw new Error("File is required");
      }

      const isImage = file.type.startsWith("image/");

      if (!isImage) {
        throw new Error("Only images  are allowed");
      }

      // STEP 1: Get presigned URL
      const presignResponse = await axios.post(
        `${API_BASE_URL}/uploads/presigned-url`,
        {
          fileName: file.name,
          type: "badges",
        },
        getAxiosConfig()
      );

      const { uploadUrl, key } = presignResponse.data.data;

      // STEP 2: Convert to BLOB
      const blob = file;

      // STEP 3: Upload to S3 using PUT
      const uploadResponse = await fetch(uploadUrl, {
        method: "PUT",
        headers: {
          "Content-Type": file.type,
        },
        body: blob,
      });

      if (!uploadResponse.ok) {
        throw new Error("Failed to upload to S3");
      }

      // STEP 4: Final URL
      const finalUrl = uploadUrl.split("?")[0];
      return { success: true, url: finalUrl, key };
    } catch (err) {
      console.error("Upload media error:", err.message);
      return { error: true, message: err.message };
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      const isImage = file.type.startsWith("image/");

      if (!isImage) {
        return;
      }

      setSelectedFile(file);

      const reader = new FileReader();
      reader.onloadend = () => {
        setFilePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleCreateBadge = async () => {
    setActionLoading(true);
    setErrorMessage("");
    setShowError(false);

    try {
      let finalFormData = { ...formData };

      if (selectedFile) {
        setUploadLoading(true);
        const uploadResult = await uploadMediaUsingPresigned(selectedFile);
        if (uploadResult.error) {
          setErrorMessage(uploadResult.message || "Failed to upload image");
          setShowError(true);
          setActionLoading(false);
          setUploadLoading(false);
          return;
        }
        finalFormData.iconUrl = uploadResult.url;
        setUploadLoading(false);
      }

      await axios.post(
        `${API_BASE_URL}/badges`,
        finalFormData,
        getAxiosConfig()
      );

      setShowCreateModal(false);
      resetForm();
      setSelectedFile(null);
      setFilePreview(null);
      fetchBadges();
    } catch (error) {
      console.error("Error creating badge:", error);

      if (error.response?.status === 409) {
        setErrorMessage(
          error.response?.data?.message ||
            `Badge with key '${formData.key}' and count ${formData.count} already exists`
        );
      } else {
        setErrorMessage(
          error.response?.data?.message ||
            "Failed to create badge. Please try again."
        );
      }
      setShowError(true);
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteBadge = async () => {
    if (!selectedBadge) return;
    setActionLoading(true);
    try {
      await axios.delete(
        `${API_BASE_URL}/badges/${selectedBadge.key}`,
        getAxiosConfig()
      );
      setShowDeleteModal(false);
      setSelectedBadge(null);
      fetchBadges();
    } catch (error) {
      console.error("Error deleting badge:", error);
    } finally {
      setActionLoading(false);
    }
  };

  const handleInitializeDefault = async () => {
    setActionLoading(true);
    try {
      await axios.post(
        `${API_BASE_URL}/badges/initialize`,
        {},
        getAxiosConfig()
      );
      fetchBadges();
    } catch (error) {
      console.error("Error initializing badges:", error);
    } finally {
      setActionLoading(false);
    }
  };

  const handleEvaluateBadges = async () => {
    setActionLoading(true);
    try {
      await axios.post(
        `${API_BASE_URL}/badges/evaluate-all`,
        {},
        getAxiosConfig()
      );
      fetchBadges();
    } catch (error) {
      console.error("Error Evaluate badges:", error);
    } finally {
      setActionLoading(false);
    }
  };
  const resetForm = () => {
    setFormData({
      key: "",
      name: "",
      description: "",
      count: 1,
      level: 0,
      iconUrl: "",
      category: "general",
      sortOrder: 0,
    });
    setSelectedFile(null);
    setFilePreview(null);
    setErrorMessage("");
    setShowError(false);
  };

  const getLevelBadge = (level) => {
    const getLevelColor = (lvl) => {
      if (lvl >= 10) return { text: "text-red-400", bg: "bg-red-500/20" };
      if (lvl >= 8) return { text: "text-orange-400", bg: "bg-orange-500/20" };
      if (lvl >= 5) return { text: "text-orange-400", bg: "bg-orange-500/20" };
      if (lvl >= 3) return { text: "text-purple-400", bg: "bg-purple-500/20" };
      if (lvl >= 1) return { text: "text-blue-400", bg: "bg-blue-500/20" };
      return { text: "text-gray-400", bg: "bg-gray-500/20" };
    };

    const colors = getLevelColor(level);
    return (
      <span
        className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${colors.text} ${colors.bg}`}
      >
        <TrendingUp size={12} />
        Level {level}
      </span>
    );
  };

  return (
    <div className={`min-h-screen ${theme.bg} p-6`}>
      <div className="max-w-7xl mx-auto space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className={`text-3xl font-bold ${theme.textPrimary}`}>
              Badge Management
            </h1>
            <p className={`${theme.textSecondary} mt-1`}>
              Create and manage badges for user achievements
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={handleEvaluateBadges}
              disabled={actionLoading}
              className={`flex items-center gap-2 px-4 py-2 ${theme.cardBg} ${theme.textPrimary} rounded-lg border ${theme.border} ${theme.hover} transition-colors disabled:opacity-50`}
              title="Trigger a re-evaluation of your badges based on your latest activity."
            >
              {actionLoading ? (
                <>
                  <RefreshCw size={18} className="animate-spin" />
                  <span>Evaluating...</span>
                </>
              ) : (
                <>
                  <RefreshCw size={18} />
                  <span>Evaluate Badges</span>
                </>
              )}
            </button>
            <button
              onClick={handleInitializeDefault}
              disabled={actionLoading}
              className={`flex items-center gap-2 px-4 py-2 ${theme.cardBg} ${theme.textPrimary} rounded-lg border ${theme.border} ${theme.hover} transition-colors disabled:opacity-50`}
            >
              <Award size={18} />
              Initialize Defaults
            </button>
            <button
              onClick={fetchBadges}
              className={`flex items-center gap-2 px-4 py-2 ${theme.cardBg} ${theme.textPrimary} rounded-lg border ${theme.border} ${theme.hover} transition-colors`}
            >
              <RefreshCw size={18} />
              Refresh
            </button>
            <button
              onClick={() => {
                resetForm();
                setShowCreateModal(true);
              }}
              className="flex items-center gap-2 px-4 py-2 bg-orange-500 text-black font-medium rounded-lg hover:bg-orange-600 transition-colors"
            >
              <Plus size={18} />
              Create Badge
            </button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div
            className={`${theme.cardBg} rounded-lg p-4 border ${theme.border}`}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className={`text-sm ${theme.textSecondary}`}>Total Badges</p>
                <p className={`text-2xl font-bold ${theme.textPrimary} mt-1`}>
                  {badges.length}
                </p>
              </div>
              <Award className="text-orange-500" size={32} />
            </div>
          </div>
        </div>

        {/* Badges Table */}
        <div
          className={`${theme.cardBg} rounded-lg border ${theme.border} overflow-hidden`}
        >
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className={theme.tableBg}>
                <tr>
                  <th
                    className={`px-6 py-3 text-left text-xs font-medium ${theme.textSecondary} uppercase tracking-wider`}
                  >
                    Badge
                  </th>
                  <th
                    className={`px-6 py-3 text-left text-xs font-medium ${theme.textSecondary} uppercase tracking-wider`}
                  >
                    Key
                  </th>

                  <th
                    className={`px-6 py-3 text-left text-xs font-medium ${theme.textSecondary} uppercase tracking-wider`}
                  >
                    Level
                  </th>
                  <th
                    className={`px-6 py-3 text-left text-xs font-medium ${theme.textSecondary} uppercase tracking-wider`}
                  >
                    Sort Order
                  </th>
                  <th
                    className={`px-6 py-3 text-left text-xs font-medium ${theme.textSecondary} uppercase tracking-wider`}
                  >
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className={`divide-y ${theme.border}`}>
                {loading ? (
                  <tr>
                    <td colSpan="6" className="px-6 py-12 text-center">
                      <RefreshCw
                        className={`animate-spin ${theme.textSecondary} mx-auto`}
                        size={24}
                      />
                    </td>
                  </tr>
                ) : badges.length === 0 ? (
                  <tr>
                    <td
                      colSpan="6"
                      className={`px-6 py-12 text-center ${theme.textSecondary}`}
                    >
                      {badges.length === 0
                        ? "No badges found. Click 'Initialize Defaults' or 'Create Badge' to get started."
                        : "No badges match your search criteria."}
                    </td>
                  </tr>
                ) : (
                  badges.map((badge) => (
                    <tr key={badge._id} className={theme.hover}>
                      <td className={`px-6 py-4 text-sm ${theme.textPrimary}`}>
                        <div className="flex items-center gap-3">
                          {badge.iconUrl ? (
                            <img
                              src={badge.iconUrl}
                              alt={badge.name}
                              className="w-10 h-10 rounded-full object-cover"
                            />
                          ) : (
                            <div className="w-10 h-10 rounded-full bg-orange-500/20 flex items-center justify-center">
                              <Award className="text-orange-500" size={20} />
                            </div>
                          )}
                          <div>
                            <div className="font-medium">{badge.name}</div>
                            {badge.description && (
                              <div
                                className={`text-xs ${theme.textSecondary} mt-0.5 max-w-xs truncate`}
                              >
                                {badge.description}
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td
                        className={`px-6 py-4 whitespace-nowrap text-sm ${theme.textSecondary}`}
                      >
                        <code
                          className={`px-2 py-1 rounded ${
                            isDarkMode
                              ? "bg-neutral-800 text-orange-400"
                              : "bg-gray-200 text-gray-800"
                          }`}
                        >
                          {badge.key}
                        </code>
                      </td>

                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        {getLevelBadge(badge.level)}
                      </td>
                      <td
                        className={`px-6 py-4 whitespace-nowrap text-sm ${theme.textSecondary}`}
                      >
                        {badge.sortOrder || 0}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => {
                              setSelectedBadge(badge);
                              setShowDeleteModal(true);
                            }}
                            className={`p-2 ${theme.inputBg} rounded-lg ${theme.hover} transition-colors`}
                            title="Delete badge"
                          >
                            <Trash2 size={16} className="text-red-400" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Create/Edit Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div
            className={`${theme.cardBg} rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto`}
          >
            <div className={`p-6 border-b ${theme.border}`}>
              <div className="flex items-center justify-between">
                <h2 className={`text-xl font-bold ${theme.textPrimary}`}>
                  {"Create New Badge"}
                </h2>
                <button
                  onClick={() => {
                    setShowCreateModal(false);
                    resetForm();
                  }}
                  className={`${theme.textSecondary} hover:${theme.textPrimary}`}
                >
                  <X size={24} />
                </button>
              </div>
            </div>

            <div className="p-6 space-y-4">
              {showError && errorMessage && (
                <div className="bg-red-500/10 border border-red-500/50 rounded-lg p-4 animate-in fade-in slide-in-from-top-2 duration-300">
                  <div className="flex items-start gap-3">
                    <AlertCircle
                      className="text-red-500 flex-shrink-0 mt-0.5"
                      size={20}
                    />
                    <div className="flex-1">
                      <h3 className="text-red-500 font-medium text-sm mb-1">
                        Cannot Create Badge
                      </h3>
                      <p className={`text-sm ${theme.textSecondary}`}>
                        {errorMessage}
                      </p>
                    </div>
                    <button
                      onClick={() => {
                        setShowError(false);
                        setErrorMessage("");
                      }}
                      className="text-red-500 hover:text-red-400 transition-colors"
                      aria-label="Close error"
                    >
                      <X size={18} />
                    </button>
                  </div>
                </div>
              )}
              <div className="grid grid-cols-2 gap-4">
                {/* Badge Key Dropdown */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label
                      className={`block text-sm font-medium ${theme.textPrimary}`}
                    >
                      Badge Key *
                    </label>
                  </div>
                  <select
                    value={formData.key}
                    onChange={(e) => {
                      const selectedOption = badgeKeyOptions.find(
                        (opt) => opt.value === e.target.value
                      );
                      setFormData({
                        ...formData,
                        key: e.target.value,
                        // Optionally auto-fill description if empty
                        description:
                          formData.description ||
                          selectedOption?.description ||
                          "",
                      });
                    }}
                    className={`w-full px-4 py-2 rounded-lg border ${
                      theme.border
                    } focus:outline-none focus:ring-2 focus:ring-orange-500 cursor-pointer transition-all ${
                      theme.inputBg
                    } ${
                      theme.inputText
                    } appearance-none bg-[right_1rem_center] bg-no-repeat disabled:opacity-50 disabled:cursor-not-allowed ${
                      isDarkMode
                        ? "bg-[url('data:image/svg+xml;charset=UTF-8,%3csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2220%22%20height%3D%2220%22%20viewBox%3D%220%200%2020%2020%22%3E%3cpath%20fill%3D%22%23999%22%20d%3D%22M5.293%207.293a1%201%200%20011.414%200L10%2010.586l3.293-3.293a1%201%200%20111.414%201.414l-4%204a1%201%200%2001-1.414%200l-4-4a1%201%200%20010-1.414z%22%2F%3E%3c%2Fsvg%3E')]"
                        : "bg-[url('data:image/svg+xml;charset=UTF-8,%3csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2220%22%20height%3D%2220%22%20viewBox%3D%220%200%2020%2020%22%3E%3cpath%20fill%3D%22%23666%22%20d%3D%22M5.293%207.293a1%201%200%20011.414%200L10%2010.586l3.293-3.293a1%201%200%20111.414%201.414l-4%204a1%201%200%2001-1.414%200l-4-4a1%201%200%20010-1.414z%22%2F%3E%3c%2Fsvg%3E')]"
                    }`}
                  >
                    <option
                      value=""
                      className={isDarkMode ? "bg-neutral-900" : "bg-white"}
                    >
                      Select a badge key...
                    </option>
                    {badgeKeyOptions.map((option) => (
                      <option
                        key={option.value}
                        value={option.value}
                        className={isDarkMode ? "bg-neutral-900" : "bg-white"}
                      >
                        {option.label} ({option.value})
                      </option>
                    ))}
                  </select>
                  <p className={`text-xs ${theme.textSecondary} mt-1`}>
                    {formData.key &&
                      badgeKeyOptions.find((opt) => opt.value === formData.key)
                        ?.description}
                  </p>
                </div>

                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label
                      className={`block text-sm font-medium ${theme.textPrimary}`}
                    >
                      Badge Name *
                    </label>
                    <span className={`text-xs ${theme.textSecondary}`}>
                      {formData.name.length}/{charLimits.name}
                    </span>
                  </div>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) =>
                      setFormData({ ...formData, name: e.target.value })
                    }
                    maxLength={charLimits.name}
                    placeholder="e.g., First Post, Email Verified"
                    className={`w-full px-4 py-2 ${theme.inputBg} ${theme.inputText} rounded-lg border ${theme.border} focus:outline-none focus:ring-2 focus:ring-orange-500`}
                  />
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <label
                    className={`block text-sm font-medium ${theme.textPrimary}`}
                  >
                    Description
                  </label>
                  <span className={`text-xs ${theme.textSecondary}`}>
                    {formData.description.length}/{charLimits.description}
                  </span>
                </div>
                <textarea
                  value={formData.description}
                  onChange={(e) =>
                    setFormData({ ...formData, description: e.target.value })
                  }
                  maxLength={charLimits.description}
                  placeholder="Enter badge description..."
                  rows={4}
                  className={`w-full px-4 py-3 rounded-lg border transition-all duration-200 resize-none
${theme.inputBg} ${theme.inputText} ${theme.border}
focus:outline-none focus:ring-2 focus:ring-orange-500/50
placeholder-gray-400`}
                  style={{
                    minHeight: "100px",
                    maxHeight: "100px",
                  }}
                />
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label
                    className={`block text-sm font-medium ${theme.textPrimary} mb-2`}
                  >
                    Count * (numbers only)
                  </label>
                  <input
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    value={formData.count}
                    onChange={(e) => {
                      const value = e.target.value.replace(/[^0-9]/g, "");
                      setFormData({
                        ...formData,
                        count: value === "" ? 0 : parseInt(value),
                      });
                    }}
                    placeholder="0"
                    className={`w-full px-4 py-2 ${theme.inputBg} ${theme.inputText} rounded-lg border ${theme.border} focus:outline-none focus:ring-2 focus:ring-orange-500`}
                  />
                  <p className={`text-xs ${theme.textSecondary} mt-1`}>
                    Milestone (min: 1)
                  </p>
                </div>

                <div>
                  <label
                    className={`block text-sm font-medium ${theme.textPrimary} mb-2`}
                  >
                    Level * (numbers only)
                  </label>
                  <input
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    value={formData.level}
                    onChange={(e) => {
                      const value = e.target.value.replace(/[^0-9]/g, "");
                      setFormData({
                        ...formData,
                        level: value === "" ? 0 : parseInt(value),
                      });
                    }}
                    placeholder="0"
                    className={`w-full px-4 py-2 ${theme.inputBg} ${theme.inputText} rounded-lg border ${theme.border} focus:outline-none focus:ring-2 focus:ring-orange-500`}
                  />
                  <p className={`text-xs ${theme.textSecondary} mt-1`}>
                    Badge priority
                  </p>
                </div>

                <div>
                  <label
                    className={`block text-sm font-medium ${theme.textPrimary} mb-2`}
                  >
                    Sort Order (numbers only)
                  </label>
                  <input
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    value={formData.sortOrder}
                    onChange={(e) => {
                      const value = e.target.value.replace(/[^0-9]/g, "");
                      setFormData({
                        ...formData,
                        sortOrder: value === "" ? 0 : parseInt(value),
                      });
                    }}
                    placeholder="0"
                    className={`w-full px-4 py-2 ${theme.inputBg} ${theme.inputText} rounded-lg border ${theme.border} focus:outline-none focus:ring-2 focus:ring-orange-500`}
                  />
                </div>
              </div>

              <div>
                <label
                  className={`block text-sm font-medium ${theme.textPrimary} mb-2`}
                >
                  Badge Media (Image)
                </label>
                <div
                  className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors ${
                    isDarkMode
                      ? "border-gray-700 hover:border-orange-500 hover:bg-neutral-900"
                      : "border-gray-300 hover:border-orange-500 hover:bg-gray-50"
                  }`}
                  onClick={() => document.getElementById("file-input").click()}
                >
                  <input
                    id="file-input"
                    type="file"
                    accept="image/*"
                    onChange={handleFileSelect}
                    className="hidden"
                  />
                  {filePreview ? (
                    <div className="space-y-3">
                      <img
                        src={filePreview}
                        alt="preview"
                        className="max-h-32 mx-auto rounded"
                      />
                      <div>
                        <p
                          className={`text-sm font-medium ${theme.textPrimary}`}
                        >
                          {selectedFile?.name}
                        </p>
                        <p className={`text-xs ${theme.textSecondary} mt-1`}>
                          Click to change
                        </p>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <Upload
                        className={`mx-auto ${theme.textSecondary}`}
                        size={32}
                      />
                      <div>
                        <p
                          className={`text-sm font-medium ${theme.textPrimary}`}
                        >
                          Click to upload or drag and drop
                        </p>
                        <p className={`text-xs ${theme.textSecondary} mt-1`}>
                          PNG, JPG, GIF up to 50MB
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div
              className={`p-6 border-t ${theme.border} flex gap-3 justify-end`}
            >
              <button
                onClick={() => {
                  setShowCreateModal(false);
                  resetForm();
                }}
                className={`px-4 py-2 ${theme.inputBg} ${theme.textPrimary} rounded-lg ${theme.hover} transition-colors`}
              >
                Cancel
              </button>
              <button
                onClick={handleCreateBadge}
                disabled={
                  actionLoading ||
                  !formData.key ||
                  !formData.name ||
                  !formData.count
                }
                className="px-4 py-2 bg-orange-500 text-black font-medium rounded-lg hover:bg-orange-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {actionLoading
                  ? "Saving..."
                  : showCreateModal
                  ? "Create Badge"
                  : "Update Badge"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && selectedBadge && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className={`${theme.cardBg} rounded-lg max-w-md w-full`}>
            <div className={`p-6 border-b ${theme.border}`}>
              <h2 className={`text-xl font-bold ${theme.textPrimary}`}>
                Delete Badge
              </h2>
            </div>

            <div className="p-6 space-y-4">
              <div className="flex items-start gap-3">
                <AlertCircle className="text-red-500 flex-shrink-0" size={24} />
                <div>
                  <p className={theme.textPrimary}>
                    Are you sure you want to delete the badge "
                    <span className="font-semibold">{selectedBadge.name}</span>
                    "?
                  </p>
                  <p className={`text-sm ${theme.textSecondary} mt-2`}>
                    This will remove the badge from all users who have earned
                    it. This action cannot be undone.
                  </p>
                </div>
              </div>
            </div>

            <div
              className={`p-6 border-t ${theme.border} flex gap-3 justify-end`}
            >
              <button
                onClick={() => {
                  setShowDeleteModal(false);
                  setSelectedBadge(null);
                }}
                className={`px-4 py-2 ${theme.inputBg} ${theme.textPrimary} rounded-lg ${theme.hover} transition-colors`}
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteBadge}
                disabled={actionLoading}
                className="px-4 py-2 bg-red-500 text-white font-medium rounded-lg hover:bg-red-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {actionLoading ? "Deleting..." : "Delete Badge"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Badges;
